import React, { useEffect } from 'react'
import { useAgeGateStore } from '@/stores/ageGateStore'

interface AgeGateProtectionProps {
  children: React.ReactNode
}

declare global {
  interface Window {
    AGEGO?: ((...args: any[]) => void) & { e?: any[][] }
    __agegoConfigured?: boolean
  }
}

const AGEGO_ENABLED = import.meta.env.PROD
const AGEGO_ASI = '880259b6-6f4d-470d-8c7c-6df1a0029415'
const AGEGO_SCRIPT_SRC = 'https://verifycdn.agego.com/v1/verify.js'

const isBot = (): boolean => {
  if (typeof navigator === 'undefined') return false

  const userAgent = navigator.userAgent.toLowerCase()
  const botPatterns = [
    'bot', 'crawl', 'spider', 'scrape', 'curl', 'wget', 'python', 'java',
    'http', 'go-http', 'node', 'googlebot', 'bingbot', 'slurp', 'duckduckbot',
    'baiduspider', 'yandexbot', 'sogou', 'exabot', 'facebookexternalhit',
    'twitterbot', 'linkedinbot', 'whatsapp', 'telegram', 'skype', 'viber',
    'feedburner', 'rss', 'validator', 'uptime', 'monitoring',
  ]

  return botPatterns.some((pattern) => userAgent.includes(pattern))
}

const AgeGateProtection: React.FC<AgeGateProtectionProps> = ({ children }) => {
  const { isVerified, markVerified, syncFromCookie } = useAgeGateStore((state) => ({
    isVerified: state.isVerified,
    markVerified: state.markVerified,
    syncFromCookie: state.syncFromCookie,
  }))

  useEffect(() => {
    // Dev : bypass
    if (!AGEGO_ENABLED) {
      markVerified({ type: 'development-bypass' })
      return
    }

    // Bot : bypass
    if (isBot()) {
      markVerified({ type: 'bot-bypass' })
      return
    }

    // Sync depuis ton propre stockage (sessionStorage)
    syncFromCookie()

    // ⚠️ NE PAS faire "return" même si isVerified est déjà true :
    // on veut quand même que AgeGO tourne pour gérer ses propres cookies / logs.
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    if (window.__agegoConfigured) return
    window.__agegoConfigured = true

    // ----- 1. Stub + configure (comme le snippet AgeGO) -----
    if (!window.AGEGO || !(window.AGEGO as any).e) {
      const stub: any = function (...args: any[]) {
        ;(stub.e = stub.e || []).push(args)
      }
      window.AGEGO = stub
    }

    window.AGEGO('configure', {
      asi: AGEGO_ASI,
      autoBlur: false,
      requireAgeVerification: true,
      overlay: {
        logo: '',
        theme: 'auto',
      },
      events: {
        onVerificationFlowEnd(eventData: unknown) {
          window.dispatchEvent(new CustomEvent('agego:verified', { detail: eventData }))
        },
        onVerifiedBefore() {
          window.dispatchEvent(new Event('agego:verified-before'))
        },
        onDirectContinue() {
          window.dispatchEvent(new Event('agego:direct-continue'))
        },
        onUnderageSkip() {
          window.dispatchEvent(new Event('agego:underage'))
        },
        onVerificationFlowFailed(error: unknown) {
          window.dispatchEvent(new CustomEvent('agego:failed', { detail: error }))
        },
      },
    })

    // ----- 2. Injecter le script si pas déjà présent -----
    let script = document.querySelector<HTMLScriptElement>(`script[src="${AGEGO_SCRIPT_SRC}"]`)
    if (!script) {
      script = document.createElement('script')
      script.src = AGEGO_SCRIPT_SRC
      script.async = true
      document.body.appendChild(script)
    }

    // ----- 3. Simuler DOMContentLoaded à la fin du chargement -----
    const onLoad = () => {
      try {
        const ev = document.createEvent('Event')
        ev.initEvent('DOMContentLoaded', true, true)
        document.dispatchEvent(ev)
      } catch (e) {
        // silencieux
      }
    }
    script.addEventListener('load', onLoad, { once: true })

    // ----- 4. Écouter les events pour ton store -----
    const handleVerified = (event: Event) => {
      const detail = (event as CustomEvent).detail
      markVerified(detail ?? { type: 'agego-verified' })
    }
    const handleVerifiedBefore = () => {
      markVerified({ type: 'verified-before' })
    }
    const handleDirectContinue = () => {
      markVerified({ type: 'direct-continue' })
    }

    window.addEventListener('agego:verified', handleVerified)
    window.addEventListener('agego:verified-before', handleVerifiedBefore)
    window.addEventListener('agego:direct-continue', handleDirectContinue)

    return () => {
      script?.removeEventListener('load', onLoad)
      window.removeEventListener('agego:verified', handleVerified)
      window.removeEventListener('agego:verified-before', handleVerifiedBefore)
      window.removeEventListener('agego:direct-continue', handleDirectContinue)
    }
  }, [markVerified, syncFromCookie])

  const showOverlay = AGEGO_ENABLED && !isVerified

  return (
    <>
      {showOverlay && <div 
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          pointerEvents: 'none',
          background: 'rgba(0, 0, 0, 0.75)',
        }}
      aria-hidden="true" />}
      {children}
    </>
  )
}

export default AgeGateProtection
